import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Loader2, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';

const PolicyRow = ({ policy, onSaved }) => {
  const [days, setDays] = useState(policy.days);
  const [busy, setBusy] = useState(false);

  const save = async (payload) => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/retention/policies/${policy.key}`, payload);
      toast.success(`Politique « ${policy.label} » mise à jour`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-between gap-4 py-3 flex-wrap" data-testid={`retention-policy-${policy.key}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm">{policy.label}</p>
          <Badge variant="outline" className={policy.enabled ? 'text-emerald-700 border-emerald-300' : 'text-muted-foreground'}>
            {policy.enabled ? 'Activée' : 'Désactivée'}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{policy.description}</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Input type="number" min={30} max={3650} value={days} className="w-24"
            onChange={(e) => setDays(e.target.value)}
            onBlur={() => { const d = parseInt(days, 10); if (d && d !== policy.days) save({ days: d }); }}
            data-testid={`retention-days-${policy.key}`} />
          <span className="text-xs text-muted-foreground">jours</span>
        </div>
        <Switch checked={policy.enabled} disabled={busy}
          onCheckedChange={(v) => save({ enabled: v })}
          data-testid={`retention-toggle-${policy.key}`} />
      </div>
    </div>
  );
};

export default function AdminRetention() {
  const [policies, setPolicies] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/admin/retention/policies`),
        axios.get(`${API}/admin/retention/runs`),
      ]);
      setPolicies(p.data);
      setRuns(r.data);
    } catch (e) { toast.error('Erreur de chargement'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/retention/run`);
      const total = data.total_purged || 0;
      toast.success(`Purge exécutée : ${total} élément(s) supprimé(s)`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la purge');
    } finally { setRunning(false); }
  };

  return (
    <AdminLayout title="Rétention des données" subtitle="Purge automatique quotidienne à 3 h 30 (Europe/Paris) — durées paramétrables">
      <div className="space-y-4" data-testid="admin-retention">
        <Card className="border-border">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Trash2 className="h-4 w-4" />Politiques de rétention</CardTitle>
            <Button size="sm" onClick={runNow} disabled={running} className="gap-2" data-testid="retention-run-btn">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}Exécuter maintenant
            </Button>
          </CardHeader>
          <CardContent className="divide-y divide-border">
            {policies === null ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : policies.map((p) => <PolicyRow key={p.key} policy={p} onSaved={fetchAll} />)}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="text-base">Historique des purges</CardTitle></CardHeader>
          <CardContent>
            {runs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune purge exécutée pour le moment.</p>
            ) : (
              <ul className="divide-y divide-border">
                {runs.map((r) => (
                  <li key={r.id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`purge-run-${r.id}`}>
                    <span>{fmtDT(r.ran_at)} <Badge variant="outline" className="ml-1">{r.trigger === 'manual' ? 'Manuelle' : 'Planifiée'}</Badge></span>
                    <span className="text-muted-foreground text-xs">
                      {Object.keys(r.results || {}).length === 0
                        ? 'Aucune politique activée'
                        : Object.entries(r.results).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
